import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useRouterState } from "@tanstack/react-router";
import type { Lang } from "./content";
import { langFromPath } from "./site";

interface LanguageContextValue {
  lang: Lang;
  dir: "ltr" | "rtl";
  t: <T extends Record<Lang, string>>(entry: T) => string;
  enquiryDraft: { topic: string; detail: string };
  setEnquiryDraft: Dispatch<SetStateAction<{ topic: string; detail: string }>>;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/**
 * Language is derived from the URL (English at root, Arabic under /ar), not from
 * localStorage. This makes each language a distinct, indexable URL and keeps SSR
 * and client render deterministic (no hydration mismatch).
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang: Lang = langFromPath(pathname);
  const dir: "ltr" | "rtl" = lang === "ar" ? "rtl" : "ltr";
  const [enquiryDraft, setEnquiryDraft] = useState({ topic: "unsure", detail: "" });
  // Retain only while switching between Contact languages. No browser storage
  // or URLs; leaving Contact or reloading discards the unsent draft.
  useEffect(() => {
    if (pathname !== "/contact" && pathname !== "/ar/contact") {
      setEnquiryDraft((draft) =>
        draft.detail || draft.topic !== "unsure" ? { topic: "unsure", detail: "" } : draft,
      );
    }
  }, [pathname]);

  const t = <T extends Record<Lang, string>>(entry: T) => entry[lang];

  return (
    <LanguageContext.Provider value={{ lang, dir, t, enquiryDraft, setEnquiryDraft }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
